import type { Agent, UserRole, UserRoleListItemResponse } from '@ag-common-lib/public-api';
import {
  catchError,
  distinctUntilChanged,
  from,
  map,
  Observable,
  of,
  scan,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import {
  currentSessionValue,
  sessionFailed,
  sessionPending,
  sessionReady,
  type SessionUid,
  type SessionValue,
} from './signed-in-session';

export type UserRolesStreamSources = {
  /** The signed-in agent per session; ready with null when the session carries no agent record. */
  agent$: Observable<SessionValue<Agent | null | undefined>>;
  /** The Firebase uid signed in right now, or null. */
  currentUid: () => SessionUid;
  /** Bumps whenever a role is granted or revoked, so an open session re-reads. */
  signal$: Observable<number>;
  fetchRoles: () => Promise<UserRoleListItemResponse[]>;
  toUserRole: (role: UserRoleListItemResponse) => UserRole;
};

/** Whether the session has an agent is all the roles read depends on. */
const toHasAgent = (entry: SessionValue<Agent | null | undefined>): SessionValue<boolean> =>
  entry.status === 'ready' ? { ...entry, value: !!entry.value } : entry;

const sameState = (left: SessionValue<boolean>, right: SessionValue<boolean>) =>
  left.uid === right.uid &&
  left.status === right.status &&
  (left.status !== 'ready' || right.status !== 'ready' || left.value === right.value);

/**
 * Builds the stream of the signed-in user's roles.
 *
 * Lives outside AuthService so that the behaviours that broke sign-in can be tested
 * directly. The service resolves three collaborators through `inject()` and calls
 * `getFirestore()` in its constructor, so constructing it in a test means standing up
 * Angular; this composition needs none of that, and it is the part that was wrong.
 *
 * The rules it encodes:
 *
 *   **No agent resolves to no roles, rather than to silence.**
 *
 * A signed-in user with no agent record is a *definite* state, not a pending one. An
 * earlier version filtered the agent for truthiness first, so for that user the stream
 * never emitted: both login gates await it with `firstValueFrom`, and the sign-in button
 * span forever with no error and nothing for the user or support to go on. An empty list
 * hands those gates an answer they already know how to refuse, and cannot widen access —
 * every caller reads roles to decide what to *allow*.
 *
 *   **Roles are never answered from a different session.**
 *
 * The roles are cached per sign-in, and a reader only gets the roles of whoever is signed
 * in when it asks. Caching only the last answer replayed the signed-out empty list to a
 * reader who asked while the new agent was still loading, which reads as "no access".
 *
 * Re-read only when the session changes or the roles signal moves. The agent itself
 * re-emits on every profile change, and none of that affects roles.
 *
 * A failed read is surfaced, since "could not read" is not "has no roles", but it does
 * not replace roles that were already read: a failed re-read keeps the last good list.
 * The next signal retries either way.
 */
export const buildUserRolesStream = ({
  agent$,
  currentUid,
  signal$,
  fetchRoles,
  toUserRole,
}: UserRolesStreamSources): Observable<UserRole[]> => {
  const roles$ = agent$.pipe(
    map(toHasAgent),
    distinctUntilChanged(sameState),
    switchMap((entry): Observable<SessionValue<UserRole[]>> => {
      const { uid } = entry;
      if (entry.status === 'pending') {
        return of(sessionPending<UserRole[]>(uid));
      }
      if (entry.status === 'failed') {
        return of(sessionFailed<UserRole[]>(uid, entry.error));
      }
      if (!entry.value) {
        return of(sessionReady<UserRole[]>(uid, []));
      }

      return signal$.pipe(
        switchMap(() =>
          from(fetchRoles()).pipe(
            map(roles => sessionReady(uid, roles.map(toUserRole))),
            catchError(error => {
              console.error('[roles] failed to read the signed-in user roles', error);
              return of(sessionFailed<UserRole[]>(uid, error));
            }),
          ),
        ),
        scan((previous, next) => (next.status === 'failed' && previous.status === 'ready' ? previous : next)),
        distinctUntilChanged(),
        startWith(sessionPending<UserRole[]>(uid)),
      );
    }),
    shareReplay(1),
  );

  return currentSessionValue(roles$, currentUid);
};
